import { observer } from 'mobx-react';
import React, { useMemo, useRef } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useStore } from '../../../store';
import useRegisterErrorField from '../../common/hooks/register-error-field-hook';

export const TermsReview = observer(() => {
  const tPrefix = 'REVIEW_AND_SIGN_UP.TERMS';
  const { t, i18n } = useTranslation('', { keyPrefix: tPrefix });
  const { quoteStore, tenantStore, reviewStore, signUpStore } = useStore();
  const { isAiOrder } = quoteStore;
  const { termsAndConditions } = tenantStore.config;
  const { aiTermsUrl, showAiTerms, showTerms, privacyPolicyUrl, showCoterminousTerms } = reviewStore;
  const { isTermAccepted, isAiTermAccepted, isCoterminousTermAccepted } = reviewStore.information;

  const { termsDescription, coterminousTermEndDate } = useMemo(() => {
    const termsDescription = (isAiOrder || !showTerms) && !showCoterminousTerms ? t('AGREE_AI') : t('AGREE');

    const dateOptions: Intl.DateTimeFormatOptions = {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      timeZone: 'UTC',
    };
    const coterminousContractEndDate =
      showCoterminousTerms && quoteStore.quoteInfo?.coterminousContractEndDate
        ? new Date(quoteStore.quoteInfo.coterminousContractEndDate)
        : null;
    const coterminousTermEndDate = coterminousContractEndDate?.toLocaleDateString(i18n.language, dateOptions);
    return { termsDescription, coterminousTermEndDate };
  }, [t, isAiOrder, showTerms, showCoterminousTerms, quoteStore.quoteInfo?.coterminousContractEndDate, i18n.language]);

  const isTermAcceptedRef = useRef<HTMLLabelElement | null>(null);
  useRegisterErrorField(isTermAcceptedRef, isTermAccepted.errorType);
  const isAiTermAcceptedRef = useRef<HTMLLabelElement | null>(null);
  useRegisterErrorField(isAiTermAcceptedRef, isAiTermAccepted.errorType);
  const isCoterminousAcceptedRef = useRef<HTMLLabelElement | null>(null);
  useRegisterErrorField(isCoterminousAcceptedRef, isCoterminousTermAccepted.errorType);

  const handleLinkClick = (e: any, url: string | undefined) => {
    e.preventDefault();
    window.open(url, `popUpWindow${url}`, 'height=700,width=800,scrollbars=yes');
  };

  return (
    <>
      {(showTerms || showAiTerms || showCoterminousTerms) && (
        <div className={'terms-container'} data-html2canvas-ignore="true">
          {showTerms && (
            <label className={'n2pc-checkbox'} ref={isTermAcceptedRef}>
              <input type="checkbox" checked={isTermAccepted.value} onChange={() => reviewStore.updateInformation('isTermAccepted')} />
              <span className={'n2pc-checkbox-mask'}></span>
              <span className={'checkbox-label'}>
                <Trans
                  i18nKey={'REVIEW_AND_SIGN_UP.TERMS.CHECKBOX_LABEL'}
                  components={[
                    <a href={termsAndConditions} onClick={e => handleLinkClick(e, termsAndConditions)} aria-label="termsandconditions"></a>,
                    <a href={privacyPolicyUrl} onClick={e => handleLinkClick(e, privacyPolicyUrl)} aria-label="privacypolicy"></a>,
                  ]}
                />
              </span>
              {isTermAccepted.errorType && signUpStore.showErrors && <label className={'error'}>{t('PLEASE_ACCEPT')}</label>}
            </label>
          )}
          {showAiTerms && (
            <label className={'n2pc-checkbox'} ref={isAiTermAcceptedRef}>
              <input type="checkbox" checked={isAiTermAccepted.value} onChange={() => reviewStore.updateInformation('isAiTermAccepted')} />
              <span className={'n2pc-checkbox-mask'}></span>
              <span className={'checkbox-label'}>
                <Trans
                  i18nKey={'REVIEW_AND_SIGN_UP.TERMS.CHECKBOX_AI_LABEL'}
                  components={[<a href={aiTermsUrl} onClick={e => handleLinkClick(e, aiTermsUrl)} aria-label="aitermsandconditions"></a>]}
                />
              </span>
              {isAiTermAccepted.errorType && signUpStore.showErrors && <label className={'error'}>{t('PLEASE_ACCEPT_AI')}</label>}
            </label>
          )}
          {showCoterminousTerms && (
            <label className={'n2pc-checkbox'} ref={isCoterminousAcceptedRef}>
              <input
                type="checkbox"
                checked={isCoterminousTermAccepted.value}
                onChange={() => reviewStore.updateInformation('isCoterminousTermAccepted')}
              />
              <span className={'n2pc-checkbox-mask'}></span>
              <span className={'checkbox-label'}>{t('CHECKBOX_COTERMINOUS_LABEL', { date: coterminousTermEndDate })}</span>
              {isCoterminousTermAccepted.errorType && signUpStore.showErrors && <label className={'error'}>{t('PLEASE_ACCEPT_AI')}</label>}
            </label>
          )}
          <p>{`${termsDescription} ${t('PROVIDE')}`}</p>
        </div>
      )}
    </>
  );
});
